import React from 'react'
import { useAppSelector } from '@app/hooks/reduxHooks'

import { Device } from '@/api/profile.api'
import { toDeviceStatus } from '@/features/dashboard/adapters/device-status.adapter'
import type { DeviceStatusType } from '@/features/dashboard/types'
import { getAllDevicesFromProfile } from '@/utils/utils'

import useDeviceSSE from './useDeviceSSE'

/* Te same role, które wpuszcza trasa dashboardu, i jedyne z uprawnieniem MQTT_SSE_READ.
   EventSource nie zdradza kodu 403, więc strumień otwarty dla innej roli (np. kierownika
   serwisu z przypisanymi urządzeniami) ponawiał się bez końca i każda próba trafiała do
   dziennika zdarzeń jako RBAC_ACCESS_DENIED. */
const TELEMETRY_STREAM_ROLES = ['ADMIN', 'USER']

export const useDeviceData = () => {
  const [devicesStatuses, setDevicesStatuses] = React.useState<DeviceStatusType[]>([])
  const [visibleDevices, setVisibleDevices] = React.useState<Device[]>([])

  const profile = useAppSelector((state) => state.profile?.data)
  const allDevices = React.useMemo<Device[]>(() => getAllDevicesFromProfile(profile), [profile])
  const canStreamTelemetry = React.useMemo(() => {
    const roles: string[] = Array.isArray(profile?.role) ? profile.role : profile?.role ? [profile.role] : []
    return roles.some((role) => TELEMETRY_STREAM_ROLES.includes(role))
  }, [profile?.role])
  const profileSerialNumbers = React.useMemo(
    () =>
      canStreamTelemetry
        ? allDevices
            .map((device) => device.serialNumber)
            .filter(
              (serialNumber): serialNumber is string => typeof serialNumber === 'string' && serialNumber.length > 0,
            )
        : [],
    [allDevices, canStreamTelemetry],
  )
  const telemetryBySerial = useDeviceSSE({ deviceIds: profileSerialNumbers })

  React.useEffect(() => {
    if (!profile) return

    setVisibleDevices(allDevices)
  }, [allDevices, profile])

  React.useEffect(() => {
    if (!profile) return

    setDevicesStatuses((prevStatuses) =>
      allDevices.map(({ id, serialNumber, alertStatus, resource }) => {
        const prevStatus = prevStatuses.find((status) => status.id === id)

        return toDeviceStatus({
          id,
          telemetry: telemetryBySerial[serialNumber],
          baseline: {
            alertStatus: prevStatus?.alertStatus ?? alertStatus ?? 'normal',
            resource: prevStatus?.resource ?? resource ?? 0,
          },
        })
      }),
    )
  }, [allDevices, profile, telemetryBySerial])

  return {
    visibleDevices,
    devicesStatuses,
    telemetryBySerial,
    setVisibleDevices,
    setDevicesStatuses,
  }
}
